import { useEffect, useMemo, useState } from 'react';
import { EMPTY_WORKSTATION_STATE, HttpStatus, QueryKeys, WorkstationState } from '@connect-x-app/shared-domain-models';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useWorkstationSessionStore, useWorkstationStore } from '../stores';
import { useGetWorkstation, useUpdateWorkstation } from './queries';

export type WorkstationType = {
  handleWorkstationChange: (value: string[] | string | Date | null | boolean, name: keyof WorkstationState) => void;
  handleWorkstationClose: () => void;
  handleWorkstationDelete: () => void;
  handleWorkstationOpen: () => void;
  handleWorkstationReset: () => void;
  handleWorkstationSubmit: () => void;
  isWorkstationChanged: boolean;
  isWorkstationOpen: boolean;
  workstation: WorkstationState | null;
  workstationDraft: WorkstationState;
};

type WorkstationHookProps = {
  onUpdateSuccess?: () => void;
};

const areWorkstationStatesEqual = (newWorkstation: WorkstationState, oldWorkstation: WorkstationState) =>
  isEqual(newWorkstation, oldWorkstation);

export const useWorkstation = ({ onUpdateSuccess }: WorkstationHookProps = {}): WorkstationType => {
  const queryClient = useQueryClient();
  const { data: workstationData, error, isError } = useGetWorkstation();
  const { setWorkstation, workstation } = useWorkstationStore();
  const { mutate } = useUpdateWorkstation(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKSTATION] });
    closeWorkstation();
    onUpdateSuccess?.();
  });
  const { isWorkstationOpen, setIsWorkstationOpen } = useWorkstationSessionStore();

  const [workstationDraft, setWorkstationDraft] = useState<WorkstationState>(workstation ?? EMPTY_WORKSTATION_STATE);

  useEffect(() => {
    if (workstationData && !isError) {
      setWorkstation(workstationData);
    } else if ((isError && error?.statusCode === HttpStatus.NOT_FOUND) || (!isError && workstationData === null)) {
      setWorkstation(EMPTY_WORKSTATION_STATE);
    }
  }, [error, setWorkstation, workstationData, isError]);

  useEffect(() => {
    setWorkstationDraft(workstation ?? EMPTY_WORKSTATION_STATE);
  }, [workstation]);

  const handleWorkstationChange = (value: string[] | string | Date | null | boolean, name: keyof WorkstationState) => {
    const convertedValue = value instanceof Date ? value.toISOString() : value;
    setWorkstationDraft(prevData => ({
      ...prevData,
      [name]: convertedValue,
    }));
  };

  const closeWorkstation = () => {
    setIsWorkstationOpen(false);
  };

  const isWorkstationChanged: boolean = useMemo(
    () => !areWorkstationStatesEqual(workstation ?? EMPTY_WORKSTATION_STATE, workstationDraft),
    [workstation, workstationDraft],
  );

  const handleWorkstationDelete = () => {
    handleWorkstationReset();
    mutate(EMPTY_WORKSTATION_STATE);
  };

  const handleWorkstationReset = () => {
    setWorkstationDraft(EMPTY_WORKSTATION_STATE);
  };

  const handleWorkstationSubmit = () => {
    mutate(workstationDraft);
  };
  const handleWorkstationOpen = () => {
    setIsWorkstationOpen(true);
  };

  const handleWorkstationClose = () => {
    setWorkstationDraft(workstation ?? EMPTY_WORKSTATION_STATE);
    closeWorkstation();
  };

  return {
    handleWorkstationChange,
    handleWorkstationClose,
    handleWorkstationDelete,
    handleWorkstationOpen,
    handleWorkstationReset,
    handleWorkstationSubmit,
    isWorkstationChanged,
    isWorkstationOpen,
    workstation,
    workstationDraft,
  };
};
